import type * as api from '@gitbook/api-client';
import { createPalette, intlPluralizeString } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { confirm, type OverlayTriggerState, toast } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { apiClient, flattenGroupedSectionGroups, type UseUpgradeSite } from '../../../state';
import { getPaidIndicatorBadgeProps } from '../PaidFeatureIndicator';
import { SiteSectionGroupIcon } from '../SiteSectionGroupIcon';
import { addSectionsToGroup } from './addSectionsToGroup';

export interface UseMoveSiteSectionsProps {
    organization: api.Organization;
    site: api.Site;
    siteStructure: api.SiteStructure;
    createSiteSectionGroupDialogTrigger: OverlayTriggerState;
    upgrade: UseUpgradeSite | null;
}

export function useMoveSiteSections(props: UseMoveSiteSectionsProps) {
    const { createSiteSectionGroupDialogTrigger, siteStructure, organization, site, upgrade } =
        props;
    return useEventCallback((siteSections: api.SiteSection[]) => {
        return createPalette({
            groups: [
                siteSections.some((section) => section.sectionGroup)
                    ? {
                          key: 'site',
                          commands: [
                              {
                                  key: 'site.root',
                                  title: 'Root of site',
                                  icon: Icon.DocsSite,
                                  trigger: () =>
                                      moveSectionsToRoot({
                                          siteSections,
                                          organizationId: organization.id,
                                          siteId: site.id,
                                      }),
                              },
                          ],
                      }
                    : null,
                {
                    key: 'groups',
                    commands:
                        siteStructure.type === 'sections'
                            ? flattenGroupedSectionGroups(siteStructure.structure).map((item) => {
                                  return {
                                      key: item.id,
                                      title: item.title,
                                      icon: <SiteSectionGroupIcon siteSectionGroup={item} />,
                                      trigger: () =>
                                          addSectionsToGroup({
                                              sectionGroup: item,
                                              siteSections,
                                              organization,
                                              site,
                                          }),
                                  };
                              })
                            : [],
                },
                {
                    key: 'new-group',
                    commands: [
                        {
                            key: 'new-group',
                            title: 'New group',
                            icon: Icon.AddSiteGroup,
                            trigger:
                                upgrade?.onPress ??
                                (() => createSiteSectionGroupDialogTrigger.open()),
                            badge: getPaidIndicatorBadgeProps({ upgrade }),
                            upgrade: Boolean(upgrade),
                        },
                    ],
                },
            ].filter((x) => x !== null),
        });
    });
}

function moveSectionsToRoot(props: {
    siteSections: api.SiteSection[];
    organizationId: string;
    siteId: string;
}) {
    const { siteSections, organizationId, siteId } = props;
    const pluralizedSection = intlPluralizeString({
        count: siteSections.length,
        singular: 'section',
        plural: 'sections',
    });

    void confirm({
        title: `Move ${pluralizedSection} to root of site?`,
        description: 'Sections in the root of a site are not grouped together in site navigation.',
        confirmLabel: 'Move',
        onConfirm: async (_event, { preventClose }) => {
            await toast.catch(
                async () => {
                    await Promise.all(
                        siteSections.map(async (section) => {
                            if (section.sectionGroup)
                                await apiClient.siteSectionGroups.removeSectionFromGroup(
                                    organizationId,
                                    siteId,
                                    section.sectionGroup,
                                    section.id
                                );
                        })
                    );
                    toast.success(`The ${pluralizedSection} have been added to group`);
                },
                `Error while adding ${pluralizedSection} to group`,
                {
                    onReject: preventClose,
                }
            );
        },
    });
}
